import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import jwt from 'jsonwebtoken';
import nookies from 'nookies';
import qs from 'qs';

import Consts from '@/constants/const';
import Tracker from '@/tracks/tracker';
import { checkApiError } from '@/utils/checkError';
import { getAppVersion } from '@/utils/common/app-utils';
import {
  isAlipayBrowser,
  isIOS,
  isMobileBrowser,
  isSayWeeeApp,
  isServer,
  isTiktokBrowser,
  isWeixinBrowser,
  safeReload
} from '@/utils/common/device';
import { compatibleParams } from '@/utils/common/params';
import { updateSessionTokenMaxAge } from '@/utils/common/session';
import { deleteCookie, setCookie } from '@/utils/cookie';
import { buildUrlWithOrigin } from '@/utils/url';
import { DEVICE_ID_V2, SIGNATURE_TOKEN } from '@weee-fe/core';

import logger from './logger';
import { isLatinoChannel } from './common/latino-app';
import { shouldTrackServerError, trackServerApiError } from './serverTracking';
import { getPageKey } from '@/tracks/getPageKey';
import { updateDeliveryExpireDtm } from '@/utils/common/porder';

export interface NewAxiosRequestConfig extends AxiosRequestConfig {
  headers?: any;
  ctx?: any;
  token?: string;
  startTime?: number;
  deliveryExpireDtm?: number | string;
  dataVersion?: number;
  noWithInHeaders?: string[];
  silentCancel?: boolean;
  enableErrorClassification?: boolean;
  pageInfo?: {
    referer: string;
    requestUrl: string;
    resolvedUrl: string;
  };
}

interface NewAxiosInstance extends AxiosInstance {
  get<T = any, R = AResponse>(url: string, params?: ObjectType, config?: NewAxiosRequestConfig): Promise<R>;
  all?<T>(values: (T | Promise<T>)[]): Promise<T[]>;
}

/**
 * 默认异常
 */
const DEFAULT_ERROR = {
  message_id: '9999',
  result: false,
  message: 'Something went wrong, Please try again later.',
  object: null
};

const classifyAxiosError = (error: any) => {
  const errorCode = error?.code;
  const errorMessage = `${error?.message || ''}`.toLowerCase();
  const responseStatus = error?.response?.status;

  if (errorCode === 'ECONNABORTED' || errorMessage.includes('timeout')) {
    return {
      error_type: 'TIMEOUT',
      message: 'Request timeout, please try again.'
    };
  }

  if (errorCode === 'ERR_NETWORK' || (!responseStatus && errorMessage.includes('network error'))) {
    return {
      error_type: 'NETWORK_ERROR',
      message: 'Network error, please check your connection and try again.'
    };
  }

  return null;
};

const BROSWER_TYPE_CONDITIONS = [
  {
    judge: isTiktokBrowser,
    value: Consts.BROWSER_TYPE_MAP.TIKTOK
  },
  {
    judge: isAlipayBrowser,
    value: Consts.BROWSER_TYPE_MAP.ALI
  },
  {
    judge: isWeixinBrowser,
    value: Consts.BROWSER_TYPE_MAP.WECHAT
  },
  {
    judge: () => true,
    value: ''
  }
];

/**
 * 需要设置的cookies
 */
const ATTRS = [
  {
    attr: [Consts.B_COOKIE],
    setAttr: Consts.B_COOKIE_IN_HEADER,
    setValue: value => value
  },
  {
    attr: [Consts.WEEE_SESSION_TOKEN],
    setAttr: Consts.WEEE_HEADER_SESSION_TOKEN,
    setValue: value => value
  },
  {
    attr: [Consts.AUTH_TOKEN],
    setAttr: Consts.AUTH_TOKEN_IN_HEADERS,
    setValue: value => `Bearer ${value}`
  },
  {
    attr: [Consts.LANG, Consts.LANGUAGE],
    setAttr: Consts.LANG,
    setValue: value => {
      if (value === Consts.LANGUAGE_ZHT) {
        return Consts.LANGUAGE_ZHT_ALIAS;
      }
      if (value === Consts.LANGUAGE_ZH_ALIAS) {
        return Consts.LANGUAGE_ZH;
      }
      if (!isLatinoChannel() && value === Consts.LANGUAGE_ES) {
        return Consts.LANGUAGE_EN;
      }
      return value;
    }
  },
  {
    attr: [Consts.WEEE_STORE],
    setAttr: Consts.WEEE_STORE,
    setValue: (value, headers) => {
      if (!isLatinoChannel() && headers[Consts.LANG] !== Consts.LANGUAGE_EN) return '';
      return value;
    }
  },
  {
    attr: [Consts.DEVICE_ID, Consts.B_COOKIE],
    setAttr: Consts.DEVICE_ID,
    setValue: value => value
  },
  {
    attr: [Consts.ZIP_CODE],
    setAttr: Consts.ZIPCODE_HEADER,
    setValue: value => value
  },
  {
    attr: [Consts.DELIVERY_DATE],
    setAttr: Consts.WEEE_DATE,
    setValue: value => value
  },
  {
    attr: [Consts.ZIP_CODE],
    setAttr: Consts.WEEE_ZIPCODE,
    setValue: value => value
  },
  {
    attr: [Consts.SALES_ORG_ID],
    setAttr: Consts.SALES_ORG_ID_HEADER,
    setValue: value => value
  },
  {
    attr: [Consts.REFERRER_ID],
    setAttr: Consts.REFERRER_ID_HEADER,
    setValue: value => value
  },
  {
    attr: [DEVICE_ID_V2],
    setAttr: DEVICE_ID_V2,
    setValue: value => value
  },
  {
    attr: [SIGNATURE_TOKEN],
    setAttr: SIGNATURE_TOKEN,
    setValue: value => value
  },
  {
    attr: ['aws-waf-token'],
    setAttr: 'x-aws-waf-token',
    setValue: value => {
      if (isServer) return '';
      return value || '';
    }
  },
  {
    attr: [Consts.OS_VERSION],
    setAttr: Consts.OS_VERSION,
    setValue: value => value
  }
];

const ADD_HADERS = [
  {
    setAttr: Consts.BROWSER_TYPE,
    setValue: (ctx?) => {
      return BROSWER_TYPE_CONDITIONS.find(({ judge }) => {
        return judge(ctx);
      }).value;
    }
  },
  {
    setAttr: 'Weee-Page-Key',
    setValue: ctx => {
      const url: string = isServer ? ctx?.req.url : location.href;
      const pageKey = getPageKey(url ?? '');
      return pageKey.replace('mweb_', '');
    }
  }
];

/**
 * 截单：响应成功后检查；`updateDeliveryExpireDtm` 用 simple 返回时刻的 nowAfter 写 Cookie。
 * 仅 message_id 10000 时触发；排除 simple 自身；拉 simple 不传触发请求的整份 config。
 */
let isRefreshing = false;
const checkCutOffTime = (config: NewAxiosRequestConfig) => {
  const reqUrl = `${config?.baseURL || ''}${config?.url || ''}`;
  if (reqUrl.includes('/so/porder/simple')) {
    return;
  }
  const now = Date.now();
  // 10000 为全局成功码，分类等页会并发多请求；simple 已更新 cookie 后，队列里旧响应仍带过期快照，必须用当前 cookie 判断，否则会重复拉 simple / safeReload
  const cookiesNow = nookies.get({}) || {};
  const endTime = Number(cookiesNow[Consts.DELIVERY_DATE_EXPIRE_DTM] || 0);
  const configSnap = Number(config.deliveryExpireDtm || 0);
  if (!endTime || now <= endTime || isRefreshing) {
    if (!endTime) {
      console.log('[delivery-cutoff] 截单 cookie 不存在，不刷新');
    }
    return;
  }
  isRefreshing = true;
  console.log('[delivery-cutoff] 截单 cookie 已过期，请求 porder/simple', {
    triggerUrl: reqUrl,
    endTime,
    configSnap,
    snapshotDiffersFromCookie: configSnap !== endTime
  });
  axios
    .get('/ec/so/porder/simple', config)
    .then(response => {
      const obj = response.data?.object;
      if (obj?.delivery_date_expire_dtm != null && obj?.server_dtm != null) {
        const nowAfter = Date.now();
        if (obj?.delivery_pickup_date) {
          setCookie(Consts.DELIVERY_DATE, obj.delivery_pickup_date);
        }
        const newEndMs = updateDeliveryExpireDtm(obj.delivery_date_expire_dtm, obj.server_dtm, nowAfter, null);
        if (newEndMs > nowAfter) {
          console.log('[delivery-cutoff] simple 已返回新截单时间，将尝试刷新页面', {
            newEndMs,
            nowAfter,
            expireRaw: obj.delivery_date_expire_dtm,
            serverDtmRaw: obj.server_dtm
          });
          safeReload('delivery-cutoff');
        } else {
          console.log('[delivery-cutoff] simple 已返回但不触发刷新（新截单不在未来）', { newEndMs, nowAfter });
        }
      } else {
        console.log('[delivery-cutoff] simple 返回缺少 delivery_date_expire_dtm / server_dtm，不刷新', {
          hasObject: !!obj
        });
      }
    })
    .catch(err => {
      console.error('[delivery-cutoff] porder/simple 请求失败', err);
    })
    .finally(() => {
      isRefreshing = false;
    });
};

/**
 * 设置header
 * @param instance
 */
const addHeaderRequestFilter = instance => {
  instance.defaults.headers.common['Content-Type'] = 'application/json;charset=UTF-8';
  instance.defaults.headers.common['Cache-Control'] = 'no-cache';

  instance.interceptors.request.use(config => {
    const ctx = config.ctx;
    const cookies = nookies.get(ctx) || {};
    const noWithInHeaders = config.noWithInHeaders || [];

    ATTRS.forEach(item => {
      let attrs = item.attr || [];
      if (noWithInHeaders) {
        attrs = attrs.filter(attr => !noWithInHeaders.includes(attr));
      }
      const selected = attrs.filter(param => !!cookies[param] || !!ctx?.req?.[param]);

      if (selected?.length) {
        const value = ctx?.req?.[selected[0]] || cookies[selected[0]];
        if (value) {
          config.headers = {
            ...config.headers,
            [item.setAttr]: value === 'undefined' ? '' : item.setValue(value, config.headers)
          };
        }
      }
    });
    //添加
    ADD_HADERS.forEach(item => {
      const value = item.setValue(ctx);
      if (value) {
        config.headers = {
          ...config.headers,
          [item.setAttr]: value
        };
      }
    });
    // if (!config.headers.Authorization && !noWithInHeaders.includes(Consts.AUTH_TOKEN)) {
    //   config.headers.Authorization = process.env.STATIC_TOKEN; //区分了build后的测试环境和生产环境
    // }

    if (isServer) {
      const ua: string = ctx?.req?.headers?.['user-agent'] || '';
      const weeeForwardedFor = `${ctx?.req?.headers?.['weee-forwarded-for']?.split(',')?.[0] || ''}`;
      if (weeeForwardedFor) {
        config.headers['X-Forwarded-For'] = weeeForwardedFor;
      } else {
        config.headers['X-Forwarded-For'] = `${ctx?.req?.headers?.['x-forwarded-for']?.split(',')?.[0] || ''}`;
      }
      config.headers['User-Agent'] = ua.endsWith('node') ? ua : `${ua} node`; //兼容app那边也会传入 node 字符串
      config.headers['X-B3-TraceId'] = ctx?.req?.headers?.['x-b3-traceid'] || '';
      config.headers['X-B3-SpanId'] = ctx?.req?.headers?.['x-b3-spanid'] || '';
    }

    if (isMobileBrowser(ctx)) {
      config.headers['Platform'] = 'h5';
      if (isAlipayBrowser(ctx) && !isServer) {
        config.headers['Weee-User-Agent'] = window.navigator.userAgent;
      }
    }
    if (isSayWeeeApp(ctx)) {
      if (isIOS(ctx)) {
        config.headers['Platform'] = 'ios';
      } else {
        config.headers['Platform'] = 'android';
      }
    }
    config.headers[Consts.APP_VERSION] = getAppVersion(ctx);
    config.headers[Consts.WEEE_CHANNEL] = process.env.CHANNEL || process.env.NEXT_PUBLIC_CHANNEL || '';
    let isTb2Env = (process.env.ENV_VAR || process.env.NEXT_PUBLIC_ENV_VAR) === 'tb2';
    if (isTb2Env) {
      config.headers['x-canary-tag'] = 'tb2';
    }
    // 保存页面信息用于埋点
    if (isServer && ctx) {
      config.pageInfo = {
        referer: ctx?.req?.headers?.referer || '',
        requestUrl: ctx?.req?.url || '',
        resolvedUrl: ctx?.resolvedUrl || ''
      };
    }

    delete config.ctx;
    config.startTime = new Date().getTime();
    config.deliveryExpireDtm = cookies['delivery_date_expire_dtm'] || 0; //截单时间

    return config;
  });
};

/**
 * 处理返回值，异常
 * @param instance
 */
const addResponseFilter = instance => {
  // popup提示账户删除信息的页面
  const delPopup = [
    '/account/login',
    '/account/signup',
    '/account/onboarding/login-with-password',
    '/account/onboarding/phone-verify'
  ];
  instance.interceptors.response.use(
    function (response: AxiosResponse<AResponse>) {
      const config: NewAxiosRequestConfig = response.config;
      const duration = new Date().getTime() - config.startTime;
      const data: AResponse = response.data;
      const errMsgId = data?.message_id;

      if (!isServer) {
        updateSessionTokenMaxAge();

        if (errMsgId !== '10000' && errMsgId !== '90500') {
          // 获取请求参数
          const requestParams = config?.params || config?.data || {};
          const obj = {
            type: 't2_status_metrics',
            params: {
              co: {
                name: 'business_exception',
                value: errMsgId,
                target: `${config?.method || ''} ${config?.url?.includes('https') ? '' : config?.baseURL || ''}${
                  config?.url || ''
                }`,
                result: 'success',
                extra: {
                  message: data?.message,
                  params: JSON.stringify(requestParams),
                  isHaveToken: !!config?.headers?.[Consts.AUTH_TOKEN_IN_HEADERS]?.replace('Bearer ', ''),
                  zipcode: config?.headers?.[Consts.WEEE_ZIPCODE],
                  deliveryDate: config?.headers?.[Consts.WEEE_DATE]
                }
              }
            }
          };
          checkApiError(obj);
        }
        if (errMsgId === '90500' && !window.location.href.includes('/503')) {
          window.location.href = buildUrlWithOrigin('/503');
          return;
        }
        if (errMsgId === 'CSM30009' || errMsgId === 'CSM30015') {
          deleteCookie(Consts.AUTH_TOKEN);
          if (!delPopup.some(item => window.location.href.includes(item))) {
            window.location.href = buildUrlWithOrigin(`/account/delete-account/deleted-tips`);
          }
        }

        if (String(errMsgId) === '10000' && !isRefreshing) {
          checkCutOffTime(config);
        }
      }
      if (isServer) {
        console.log(`[API] ${config.url} ${duration}ms`);

        // 🎯 服务端埋点逻辑：当响应失败时记录错误
        if (shouldTrackServerError(errMsgId)) {
          trackServerApiError({ message_id: errMsgId, message: data?.message || '' }, config);
        }

        // logger.info({
        //   method: response?.request?.method,
        //   api: `${response?.config?.baseURL}${response?.config?.url}`,
        //   headers: JSON.stringify(response?.config?.headers),
        //   params: JSON.stringify(response?.config?.data),
        //   response: JSON.stringify(data),
        //   duration: duration
        // });
      }
      return Promise.resolve(data);
    },
    function (error) {
      if (axios.isCancel(error)) {
        const isSilentCancel = Boolean(error?.config?.silentCancel);
        return Promise.resolve({
          ...DEFAULT_ERROR,
          ...(isSilentCancel
            ? {
                message: 'REQUEST_CANCELED',
                __CANCEL__: true
              }
            : {})
        });
      }

      const requestConfig: NewAxiosRequestConfig = error?.config || error?.response?.config || {};
      const shouldClassifyError = Boolean(requestConfig.enableErrorClassification);
      const classifiedError = shouldClassifyError ? classifyAxiosError(error) : null;
      if (classifiedError) {
        return Promise.resolve({
          ...DEFAULT_ERROR,
          ...classifiedError
        });
      }

      if (!isServer) {
        const isPreheat = window.location.href.includes('/support/preheat');
        if (isPreheat) {
          return Promise.resolve({
            ...DEFAULT_ERROR,
            message: 'Request blocked on preheat page'
          });
        }
        const status = error?.response?.status;
        const config = error?.response?.config;
        const requestParams = config?.params || config?.data || {};
        const obj = {
          type: 't2_status_metrics',
          params: {
            co: {
              name: 'business_exception',
              value: status,
              target: `${config?.method || ''} ${config?.url?.includes('https') ? '' : config?.baseURL || ''}${
                config?.url || ''
              }`,
              result: 'success',
              extra: {
                message: error?.response?.data?.message,
                params: JSON.stringify(requestParams),
                isHaveToken: !!config?.headers?.[Consts.AUTH_TOKEN_IN_HEADERS]?.replace('Bearer ', ''),
                zipcode: config?.headers?.[Consts.WEEE_ZIPCODE],
                deliveryDate: config?.headers?.[Consts.WEEE_DATE]
              }
            }
          }
        };
        checkApiError(obj);
        switch (status) {
          case 403:
          case 401:
            deleteCookie(Consts.AUTH_TOKEN);
            deleteCookie(Consts.IS_LOGIN);
            if (!delPopup.some(item => window.location.href.includes(item))) {
              window.location.href = buildUrlWithOrigin(
                `/account/login?redirect_url=${encodeURIComponent(window.location.href)}`
              );
            }
            break;
          default:
            try {
              if (status >= 400) {
                axios.post(`${process.env.API_HOST || process.env.NEXT_PUBLIC_API_HOST}/ec/content/logs`, {
                  method: `${error?.request?.method}`,
                  api: `${error?.config?.url?.includes('https') ? '' : error?.config?.baseURL}${error?.config?.url}`,
                  data: JSON.stringify(error?.config?.data),
                  headers: JSON.stringify(error?.config?.headers),
                  err_msg: JSON.stringify(error?.response)
                });
              }
            } catch (error) {}

            break;
        }
      }
      if (isServer) {
        const config: NewAxiosRequestConfig = error.config || {};
        const response = error?.response || {};
        const duration = new Date().getTime() - config.startTime;
        try {
          const { Authorization, ...headers } = config?.headers || {};
          logger.error({
            method: error?.request?.method,
            api: `${config?.url?.includes('https') ? '' : config?.baseURL}${config?.url}`,
            headers: JSON.stringify(headers),
            message: `${response?.message}`,
            data: JSON.stringify(response.data || ''),
            status: `${response.status}`,
            params: JSON.stringify(config?.data),
            dataMessage: JSON.stringify(response.data?.error || ''),
            duration: duration
          });
        } catch (error) {}
      }

      return Promise.resolve(DEFAULT_ERROR);
    }
  );
};

/**
 *  method proxy
 * @param instance
 */
const addGetMethodFilter = instance => {
  const _get = instance.get;
  instance.get = (url, params, config) => {
    const { cache = false, namespace = '', ...rest } = compatibleParams(params) || {};
    if (!isServer) {
      const isPreheat = window.location.href.includes('/support/preheat');
      if (isPreheat) {
        return Promise.resolve({
          ...DEFAULT_ERROR,
          message: 'GET request blocked on preheat page'
        });
      }
    }
    if (!isServer && cache) {
      const key = `${url}`;
      const clearCache = cache === 'restore';
      const data = namespace ? window?.[namespace]?.[key] : window[key];
      if (data && !clearCache) {
        return data;
      }

      let promise = _get(`${url}${qs.stringify(rest, { addQueryPrefix: true })}`, config).catch(() => {
        if (namespace) {
          delete window?.[namespace]?.[key];
        } else {
          delete window[key];
        }
      });
      if (namespace) {
        if (window?.[namespace]) {
          window[namespace][key] = promise;
        } else {
          window[namespace as string] = {
            [key]: promise
          };
        }
      } else {
        window[key] = promise;
      }
      return promise;
    }
    return _get(`${url}${qs.stringify(params, { addQueryPrefix: true })}`, config);
  };
};

export const createInstance = () => {
  const instance: NewAxiosInstance = axios.create({
    withCredentials: false,
    timeout: 10000,
    baseURL: process.env.API_HOST || process.env.NEXT_PUBLIC_API_HOST,
    headers: {
      Platform: 'pc'
    }
  });

  addGetMethodFilter(instance);
  addHeaderRequestFilter(instance);
  addResponseFilter(instance);

  instance.all = axios.all;

  return instance;
};

export default createInstance();
